import { useEffect, useState } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { AgencySearchQuery } from '../../../generated/graphql';
import { notEmpty } from '../../../utils';
import { SEARCH_QUERY, PAGE_SIZE } from '../search-query';
import { SearchResultsState } from '../utils/get-search-from-query';
import isPropertyManagerFilter from '../utils/is-property-manager-filter';

type SearchState = SearchResultsState | null;

type LocationProfiles = NonNullable<AgencySearchQuery['locationProfiles']>;

interface UseSearchResults {
  data?: AgencySearchQuery;
  loading: boolean;
  error?: ApolloError;
  /** Profiles the API resolved, ignoring any bad slugs in the URL. */
  actualLocationProfiles: LocationProfiles;
  /** Whichever of the two searches the current view is showing. */
  search?:
    | AgencySearchQuery['agencySearchBySuburbs']
    | AgencySearchQuery['contactSearchBySuburbs'];
  agencyIds: number[];
}

const useSearchResults = (state: SearchState): UseSearchResults => {
  // Persist data so the UI is retained while a client-side page loads
  const [localData, setLocalData] = useState<AgencySearchQuery>();

  const {
    data: queryData,
    loading,
    error,
  } = useQuery<AgencySearchQuery>(SEARCH_QUERY, {
    variables: {
      suburbSlugs: state?.suburbs,
      page: state?.page,
      pageSize: PAGE_SIZE,
      isAgencyView: state?.isAgency,
      keywordName: state?.keywordName,
      // Contact parameters
      contactSortBy: state?.sortBy, // TODO: url is set as startCase, avoid breaking change
      agentFilter: isPropertyManagerFilter(state?.agentFilter)
        ? 'rent'
        : 'sale',
      // Agency parameters
      agencySortBy: state?.sortBy, // TODO: url is set as startCase, avoid breaking change
      includeAgents: true,
    },
    errorPolicy: 'all', // https://www.apollographql.com/docs/react/v2/data/error-handling/#error-policies
  });

  useEffect(() => {
    if (!loading && queryData) {
      setLocalData(queryData);
    }
  }, [loading, queryData]);

  // Preference query data, but fall back to local data (eg. when moving
  // between pages, query data will be empty)
  const data = queryData || localData;

  const actualLocationProfiles =
    data?.locationProfiles?.filter((profile) => !!profile?.urlSlug) ?? [];

  const search = state?.isAgency
    ? data?.agencySearchBySuburbs
    : data?.contactSearchBySuburbs;

  const agencyIds = state?.isAgency
    ? data?.agencySearchBySuburbs?.results?.map((agency) => agency?.id)
    : data?.contactSearchBySuburbs?.results?.map(
        (contact) => contact?.agencyId,
      );

  return {
    data,
    loading,
    error,
    actualLocationProfiles,
    search,
    agencyIds: [...new Set(agencyIds?.filter(notEmpty) ?? [])],
  };
};

export default useSearchResults;
